import React, { useState, useEffect, useMemo } from 'react';
import { Button, Modal, OverlayTrigger, Tooltip, Form, Row, Spinner } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import {
  getPercentageESG,
  getPercentageSROI,
  putChangeMonitoringStatusOnSubmitted,
  logout,
} from '@lib/api';
import { SuccessToast, DangerToast, InfoToast } from '@components/GlobalToastList';
import LoadingSpinner from '@components/loading-spinner';
import TutorialsButtonWrapper from 'components/TutorialsButtonWrapper';
import ProjectDonutChart from 'components/charts/projectDonutChart';

const MonitoringHeader = ({
  activeStep,
  projectId,
  projectStatus,
  sectorCode,
  interventionClassCode,
  proponentCode,
  vanBenefits,
  translations,
  isUpdated,
  setIsUpdated,
  clientId,
  requestId,
  updatedAt,
  isLoading,
  setIsLoading,
  isMonitoring = false,
  isInMonitoring = false,
  monitoringVersion,
  projectMonitoringVersion,
}) => {
  const [progress, setProgress] = useState({});
  const [dataGeneral, setDataGeneral] = useState({});
  const [dataESG, setDataESG] = useState({});
  const [dataSROI, setDataSROI] = useState({});
  const [donutProgress, setDonutProgress] = useState([]);
  const [showModalAlert, setShowModalAlert] = useState(false);
  const [showModalCheckVanBenefits, setShowModalCheckVanBenefits] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [isSubmittingLoading, setIsSubmittingLoading] = useState(false);
  const history = useHistory();
  const fullDate = useMemo(() => new Date(updatedAt), [updatedAt]);
  const date = `${fullDate.getDate()}-${fullDate.getMonth() + 1}-${fullDate.getFullYear()}`;
  const hours = `${fullDate.getHours()}:${fullDate.getMinutes()}`;

  // helper to keep the original “array render” behavior intact
  const tr = (code, field = 'text') =>
    translations?.filter((t) => t.code === code)?.map((t) => t[field]);

  const isSubmitted = projectStatus === 'SUBMITTED';
  const stepIs = (step) => activeStep === step;

  const sroiIsDisabled =
    sectorCode === 'EVENTI' ||
    proponentCode === 'ET' ||
    proponentCode === 'LIGHTPRO' ||
    proponentCode === 'LIGHTNOPRO' ||
    interventionClassCode === 'NOINTPURCH';

  const shouldForceSROI = proponentCode === 'ET' || interventionClassCode === 'NOINTPURCH';

  const handleCloseModal = (setShowModal) => {
    setShowModal(false);
    setIsChecked(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let cancelled = false;
    const reqId = Symbol();
    let lastReqId = reqId;

    const clamp = (n) => Math.max(0, Math.min(100, Number.isFinite(n) ? n : 0));

    const setDefaults = () => {
      if (cancelled) return;
      setProgress({
        general: { total: 100, done: 100, notdone: 0, threshold: 80 },
        esg: { total: 100, done: 0, notdone: 100, threshold: 40 },
        sroi: { total: 100, done: 0, notdone: 100, threshold: 100 },
      });
    };

    const load = async () => {
      if (!projectId && !isUpdated) {
        setDefaults();
        return;
      }

      try {
        const [sroiRaw, esgRaw] = await Promise.all([
          getPercentageSROI(projectId, monitoringVersion),
          getPercentageESG(projectId, monitoringVersion),
        ]);

        if (cancelled || lastReqId !== reqId) return;

        const sroi = clamp(sroiRaw?.percentage ?? 0);
        const esg = clamp(esgRaw ?? 0);

        setProgress({
          general: { total: 100, done: 100, notdone: 0, threshold: 80 },
          esg: { total: 100, done: esg, notdone: 100 - esg, threshold: 40 },
          sroi: { total: 100, done: sroi, notdone: 100 - sroi, threshold: 100 },
        });
      } catch {
        setDefaults();
      } finally {
        if (!cancelled) setIsUpdated(false);
      }
    };

    load();

    return () => {
      cancelled = true;
      lastReqId = Symbol(); // mark obsolete
    };
  }, [projectId, isUpdated, monitoringVersion, setIsUpdated]);

  useEffect(() => {
    if (!progress?.general || !progress?.esg || !progress?.sroi) return;

    const sroiData = shouldForceSROI ? { ...progress.sroi, done: 100, notdone: 0 } : progress.sroi;

    setDataGeneral(progress.general);
    setDataESG(progress.esg);
    setDataSROI(sroiData);
    setDonutProgress([progress.general.done, progress.esg.done, sroiData.done]);
  }, [progress, shouldForceSROI]);

  const checkVanBenefits = () => {
    // if (interventionClassCode !== 'NOINTPURCH' && (vanBenefits <= 0 || !vanBenefits)) {
    //   setShowModalCheckVanBenefits(true);
    //   return;
    // }
    setShowModalAlert(true);
  };

  const changeToSubmitted = async (e) => {
    e.preventDefault();
    // --- original submission logic kept commented for parity ---
    setIsSubmittingLoading(true);
    InfoToast('Salvataggio...', 'Stiamo apportando le modifiche', false);
    try {
      await putChangeMonitoringStatusOnSubmitted(projectId, 30000);
      setIsSubmittingLoading(false);
      if (localStorage.getItem('ics_role') === '2' || localStorage.getItem('ics_role') === '3') {
        SuccessToast('Operazione conclusa', 'Progetto inviato correttamente');
      }
      setTimeout(() => {
        if (localStorage.getItem('ics_role') === '1') {
          logout();
          history.push(`/`);
        } else if (
          localStorage.getItem('ics_role') === '2' ||
          localStorage.getItem('ics_role') === '3'
        ) {
          history.push('/monitoraggio');
        }
      }, 1000);
    } catch {
      setIsSubmittingLoading(false);
      DangerToast('Attenzione', 'Errore nella sottomissione del progetto');
    }
  };

  const allDone = donutProgress.every((v) => v === 100);
  const disableSubmit = donutProgress.length > 0 || isLoading ? !allDone || isLoading : true;

  const baseHref = useMemo(() => {
    return `/monitoraggio/${projectId}/ver=${monitoringVersion}`;
  }, [monitoringVersion, projectId]);

  const StepCard = ({
    index,
    titleCode,
    href,
    chartId,
    data,
    isCurrent,
    showSave,
    formId,
    isLoading,
  }) => (
    <div className='chartBox impactChart mx-4 my-3'>
      <a href={href}>
        <h5 style={chartId === 'esg' ? { whiteSpace: 'nowrap' } : undefined}>
          {index} {tr(titleCode)}
        </h5>
      </a>

      {Object.keys(dataGeneral).length > 0 &&
      (chartId === 'general' ? Object.keys(progress).length > 0 : true) ? (
        <ProjectDonutChart
          chartId={chartId}
          data={data}
          current={isCurrent}
          progress={progress}
          style={{ marginTop: '1rem' }}
        />
      ) : null}

      {showSave && (
        <Button form={formId} id='saveAll' type='submit' className='mt-3' disabled={isLoading}>
          {!isLoading ? (
            'SALVA'
          ) : (
            <span className='d-flex align-items-center' style={{ gap: '0.5rem' }}>
              Caricamento
              <Spinner animation='border' size='sm' />
            </span>
          )}
        </Button>
      )}
    </div>
  );

  const checkLabel = (
    <>
      <p className='check-text mb-4'>
        “{tr('ICS108', 'description')}&nbsp;
        <a
          className='a-check-text'
          href={tr('ICS144', 'description')}
          target='_blank'
          rel='noreferrer'>
          {tr('ICS144')}
        </a>
        <span>{tr('ICS110', 'description')}</span>
      </p>
      <p className='check-text'>{tr('ICS111', 'description')}&nbsp;</p>
    </>
  );

  return (
    <div className='simple-box impact mt-0'>
      <div className='d-flex flex-wrap justify-content-center'>
        {/* 1. Generali */}
        <StepCard
          index='1.'
          titleCode='ICS001'
          href={baseHref}
          chartId='general'
          data={dataGeneral}
          isCurrent={stepIs('general')}
          showSave={stepIs('general') && !isSubmitted && !isInMonitoring}
          formId='dati-generali-form'
        />

        {/* 2. ESG */}
        <StepCard
          index='2.'
          titleCode='ICS029'
          href={projectId ? `${baseHref}/esg` : '#'}
          chartId='esg'
          data={dataESG}
          isCurrent={stepIs('esg')}
          showSave={
            (stepIs('esg') && !isSubmitted) ||
            (stepIs('esg') &&
              isInMonitoring &&
              +monitoringVersion === projectMonitoringVersion &&
              isMonitoring)
          }
          formId='valutazione-esg-form'
          isLoading={isLoading}
        />

        {/* 3. SROI (conditionally interactive) */}
        <div
          className='chartBox impactChart mx-4 my-3'
          style={{ pointerEvents: sroiIsDisabled ? 'none' : '' }}>
          <a href={projectId ? `${baseHref}/sroi` : '#'}>
            <h5>3. {tr('ICS017')}</h5>
          </a>

          {Object.keys(dataGeneral).length > 0 ? (
            <ProjectDonutChart
              chartId='sroi'
              data={dataSROI}
              current={stepIs('sroi')}
              progress={progress}
              style={{ marginTop: '1rem' }}
            />
          ) : null}

          {((stepIs('sroi') && proponentCode !== 'AUD' && !isSubmitted) ||
            (stepIs('sroi') &&
              isInMonitoring &&
              +monitoringVersion === projectMonitoringVersion &&
              isMonitoring &&
              proponentCode !== 'AUD')) && (
            <Button
              form='valutazione-sroi-form'
              id='saveAll'
              type='submit'
              className='mt-3'
              disabled={isLoading}>
              {!isLoading ? (
                'SALVA'
              ) : (
                <span className='d-flex align-items-center' style={{ gap: '0.5rem' }}>
                  Caricamento
                  <Spinner animation='border' size='sm' />
                </span>
              )}
            </Button>
          )}
        </div>

        {/* 4. Invio */}
        <div className='chartBox impactChart mx-4 my-3'>
          <h5>4.{tr('ICS030')}</h5>

          {isInMonitoring && +monitoringVersion === projectMonitoringVersion && isMonitoring ? (
            <div className='d-flex justify-content-center align-items-center flex-column'>
              <OverlayTrigger
                placement='top'
                delay={{ show: 200, hide: 400 }}
                overlay={
                  <Tooltip id='button-tooltip'>
                    Per inviare è necessario che tutte le sezioni siano completate al 100%
                  </Tooltip>
                }
                {...(allDone ? { show: false } : null)}>
                <span className='d-inline-block' style={{ margin: '4rem auto 2rem auto' }}>
                  <Button
                    size='lg'
                    type='submit'
                    style={!allDone ? { pointerEvents: 'none' } : null}
                    onClick={checkVanBenefits}
                    disabled={disableSubmit}>
                    INVIA
                  </Button>
                </span>
              </OverlayTrigger>

              {allDone && (
                <TutorialsButtonWrapper
                  isThanks
                  url={'https://www.youtube.com/embed/ktF2799YdK0?si=GbWTlGVcT9-Fidor'}
                  videoTitle={'Tutorial Sottomissione Progetto'}
                />
              )}
            </div>
          ) : (
            <>
              <Row className='flex-column align-items-center' style={{ marginTop: '4.5rem' }}>
                <Button size='lg' id='submitted-button' variant='outline-dark' disabled>
                  INVIATO
                </Button>
              </Row>
            </>
          )}
        </div>
      </div>
      {/* Confirm submit */}
      <Modal
        size='lg'
        show={showModalAlert}
        onHide={() => handleCloseModal(setShowModalAlert)}
        centered>
        {isSubmittingLoading && <LoadingSpinner />}
        <Modal.Header closeButton>
          <Modal.Title>Attenzione</Modal.Title>
        </Modal.Header>
        <Modal.Body className='px-5'>
          <p className='mb-4'>
            Effettuando la sottomissione non sarai più in grado di modificare i dati generali, le
            valutazioni e la compilazione del sondaggio associato.
          </p>
          <div>
            <Form.Group className='mb-3' controlId='checkPreSubmit'>
              <Form.Check
                type='checkbox'
                label={checkLabel}
                id='checkPreSubmit'
                onChange={() => setIsChecked((v) => !v)}
              />
            </Form.Group>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='success' onClick={() => handleCloseModal(setShowModalAlert)}>
            Annulla
          </Button>
          <Button variant='primary' disabled={!isChecked} onClick={changeToSubmitted}>
            Conferma
          </Button>
        </Modal.Footer>
      </Modal>
      {/* VAN benefits guardrail */}
      <Modal
        size='lg'
        show={showModalCheckVanBenefits}
        onHide={() => handleCloseModal(setShowModalCheckVanBenefits)}
        centered>
        <Modal.Header closeButton>
          <Modal.Title>Attenzione</Modal.Title>
        </Modal.Header>
        <Modal.Body className='px-5'>
          <p className='my-3'>{tr('ICS117')}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='success' onClick={() => handleCloseModal(setShowModalCheckVanBenefits)}>
            Chiudi
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MonitoringHeader;
